import { useEffect, useState, type ReactNode } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Self-hosted World Labs Marble .spz via Spark.
 *
 * NEVER use THREE.Box3.setFromObject on a SplatMesh — splat points are not
 * ordinary BufferGeometry, so the box is empty. Use SplatMesh.getBoundingBox().
 *
 * Marble worlds arrive at arbitrary origin/scale. Centering the bbox mid-point
 * at y=0 puts the camera inside the cloud (smear / beige void). We instead:
 *   1) measure at unit scale
 *   2) scale so max(XZ) ≈ targetSpan metres
 *   3) floor-align (min.y → 0) and centre XZ
 * Authored splatTransform scale/position/rotation are applied on top.
 *
 * While loading, optionally show the world's pano.jpg on an inward sphere so
 * the learner never sees Lego or an empty void.
 */
const DEFAULT_TARGET_SPAN_M = 6.0;

export function SplatWorld({
  url,
  panoUrl,
  rotationDeg = [0, 0, 0],
  scale = 1,
  position = [0, 0, 0],
  autoFit = true,
  targetSpanM = DEFAULT_TARGET_SPAN_M,
  fallback = null,
}: {
  url: string;
  /** Equirectangular pano shown while the .spz loads (same world folder). */
  panoUrl?: string;
  rotationDeg?: [number, number, number];
  scale?: number;
  position?: [number, number, number];
  autoFit?: boolean;
  targetSpanM?: number;
  fallback?: ReactNode;
}) {
  const { gl, scene } = useThree();
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let disposed = false;
    let sparkRenderer: { dispose?: () => void } | null = null;
    let splat: { dispose?: () => void } | null = null;
    let panoMesh: THREE.Mesh | null = null;
    setReady(false);
    setFailed(false);

    const setStage = (s: string) =>
      ((window as unknown as { __splatStage?: string }).__splatStage = s);

    const clearPano = () => {
      if (!panoMesh) return;
      try {
        scene.remove(panoMesh);
        const mat = panoMesh.material as THREE.MeshBasicMaterial;
        mat.map?.dispose();
        mat.dispose();
        panoMesh.geometry.dispose();
      } catch {
        /* noop */
      }
      panoMesh = null;
    };

    (async () => {
      try {
        // Soft placeholder so intro isn't empty while Spark boots.
        if (panoUrl) {
          setStage("pano-loading");
          try {
            const tex = await new THREE.TextureLoader().loadAsync(panoUrl);
            tex.colorSpace = THREE.SRGBColorSpace;
            // UV skybox — not EquirectangularReflectionMapping (envMap-only).
            if (disposed) {
              tex.dispose();
              return;
            }
            panoMesh = new THREE.Mesh(
              new THREE.SphereGeometry(12, 64, 32),
              new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, depthWrite: false }),
            );
            panoMesh.position.set(0, 1.4, 0);
            scene.add(panoMesh);
            setStage("pano-ready");
          } catch (e) {
            setStage("pano-fail:" + ((e as Error)?.message || String(e)));
          }
        }

        setStage("importing");
        const spark: any = await import("@sparkjsdev/spark");
        if (disposed) return;
        setStage("imported");

        sparkRenderer = new spark.SparkRenderer({ renderer: gl });
        scene.add(sparkRenderer as unknown as THREEObject);
        setStage("renderer-added");

        splat = new spark.SplatMesh({
          url,
          onLoad: () => setStage("onLoad"),
          onProgress: (e: ProgressEvent) =>
            setStage("progress:" + (e.loaded || 0) + "/" + (e.total || 0)),
        });
        const m = splat as unknown as {
          rotation: { set: (x: number, y: number, z: number) => void };
          scale: { setScalar: (s: number) => void };
          position: { set: (x: number, y: number, z: number) => void };
          initialized?: Promise<unknown>;
          getBoundingBox?: (centersOnly?: boolean) => THREE.Box3;
        };
        const d2r = (d: number) => (d * Math.PI) / 180;
        // Measure at unit scale + authored rotation only.
        m.rotation.set(d2r(rotationDeg[0]), d2r(rotationDeg[1]), d2r(rotationDeg[2]));
        m.scale.setScalar(1);
        m.position.set(0, 0, 0);
        scene.add(splat as unknown as THREEObject);
        setStage("scene-added");

        if (m.initialized) {
          setStage("awaiting-initialized");
          await m.initialized;
          setStage("initialized-ok");
        } else {
          setStage("no-initialized-prop");
          await new Promise((r) => setTimeout(r, 1000));
        }
        if (disposed) return;

        let box: THREE.Box3 | null = null;
        try {
          if (typeof m.getBoundingBox === "function") {
            box = m.getBoundingBox(true);
            setStage("bbox-spark");
          }
        } catch (e) {
          setStage("bbox-spark-err:" + ((e as Error)?.message || String(e)));
        }

        if (box && !box.isEmpty()) {
          const c = box.getCenter(new THREE.Vector3());
          const s = box.getSize(new THREE.Vector3());
          (window as unknown as { __splatBox?: unknown }).__splatBox = {
            empty: false,
            min: [box.min.x, box.min.y, box.min.z],
            max: [box.max.x, box.max.y, box.max.z],
            center: [c.x, c.y, c.z],
            size: [s.x, s.y, s.z],
            source: "getBoundingBox",
          };

          let fitScale = scale;
          let px = position[0];
          let py = position[1];
          let pz = position[2];

          if (autoFit && s.length() > 0.05) {
            // Use the SMALLER horizontal extent. Marble bboxes often include a
            // long outlier axis (hallway floaters); fitting the MAX axis crushed
            // this office to ~1.5 m wide and left the WASD camera in a black void.
            const shorter = Math.min(s.x, s.z);
            const longer = Math.max(s.x, s.z);
            const horiz =
              shorter > 0.5 && longer / shorter > 2.2
                ? Math.max(shorter * 1.2, 0.001)
                : Math.max(longer, 0.001);
            fitScale = (targetSpanM / horiz) * scale;
            // Floor at y=0, XZ centred — NOT mid-height centred (that puts
            // eye-level cameras inside the gaussian cloud → smear).
            px = -c.x * fitScale + position[0];
            py = -box.min.y * fitScale + position[1];
            pz = -c.z * fitScale + position[2];
            setStage("auto-fitted");
          }

          m.scale.setScalar(fitScale);
          m.position.set(px, py, pz);

          const fittedSize = [
            s.x * fitScale,
            s.y * fitScale,
            s.z * fitScale,
          ] as [number, number, number];
          (window as unknown as { __splatFit?: unknown }).__splatFit = {
            fitScale,
            position: [px, py, pz],
            sizeM: fittedSize,
            targetSpanM,
            // Suggested establish: stand near +Z looking toward desk (-Z).
            suggestEstablish: {
              position: [
                fittedSize[0] * 0.22,
                Math.min(1.65, fittedSize[1] * 0.55),
                fittedSize[2] * 0.38,
              ],
              target: [0, Math.min(1.15, fittedSize[1] * 0.4), -fittedSize[2] * 0.12],
              fov: 52,
            },
            suggestDesk: {
              position: [
                fittedSize[0] * 0.05,
                Math.min(1.25, fittedSize[1] * 0.42),
                fittedSize[2] * 0.08,
              ],
              target: [0, Math.min(1.05, fittedSize[1] * 0.38), -fittedSize[2] * 0.28],
              fov: 40,
            },
          };
        } else {
          m.scale.setScalar(scale);
          m.position.set(position[0], position[1], position[2]);
          (window as unknown as { __splatBox?: unknown }).__splatBox = {
            empty: true,
            source: "unavailable",
          };
          setStage("bbox-skipped-showing-splat");
        }

        // Drop the loading pano once the splat is fitted. Keeping it made WASD
        // feel broken: a skybox has no parallax, so walking looks like "look only".
        if (box && !box.isEmpty()) {
          clearPano();
          if (!disposed) setReady(true);
          setStage("ready-splat");
        } else {
          // No bbox — keep pano so the learner isn't left on black.
          if (!disposed) setReady(true);
          setStage("ready-pano-fallback");
        }
      } catch (err) {
        if (!disposed) {
          const msg = (err as Error)?.stack || (err as Error)?.message || String(err);
          console.warn("[sim3d] Spark splat load failed (check CSP wasm / data:).", err);
          (window as unknown as { __splatErr?: string }).__splatErr = msg;
          setStage("failed");
          // Keep pano if it loaded — never end on a black void.
          setFailed(!panoMesh);
          if (panoMesh) setReady(true);
        }
      }
    })();

    return () => {
      disposed = true;
      clearPano();
      try {
        if (splat) {
          scene.remove(splat as unknown as THREEObject);
          splat.dispose?.();
        }
        if (sparkRenderer) {
          scene.remove(sparkRenderer as unknown as THREEObject);
          sparkRenderer.dispose?.();
        }
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    url,
    panoUrl,
    scale,
    position[0],
    position[1],
    position[2],
    rotationDeg[0],
    rotationDeg[1],
    rotationDeg[2],
    autoFit,
    targetSpanM,
  ]);

  if (failed || !ready) return <>{fallback}</>;
  return null;
}

type THREEObject = Parameters<import("three").Scene["add"]>[0];
